import { defineStore } from "pinia";
import api from "@/modules/api";
import { ChatMessage } from "@/types/entity";
import { Response, ResponseError } from "@/types/api-types";
import { useChatSession } from "@/stores/chatSession";
import { useMateTraitMedia } from "@/stores/mateTraitMedia";
import { AxiosError, CancelTokenSource } from "axios";
import { ChatMessageImageType, InputTypeEnum } from "@/enums/chatEnums";
import { ApiGetChatReplyOptions, createOneChatImageMessage } from "@/apis/chat";
import { useMate } from "@/stores/mate";
import { useGlobalUser } from "./globalUser";

interface ChatState {
  sessionId2MessageList: Record<number, { keyList: number[] }>; // 会话id对应的消息列表
  messageMap: Record<number, ChatMessage>; // 消息id对应的消息
  showInsufficientCrystalPopup: boolean;
  showInsufficientPowerPopup: boolean;
  missCrystalAmount: number;
  missPowerAmount: number;
  exploreScrollY: number;
  prevGeneratedMsgId: number;
  replyOptions: string[];
  replyGenerating: boolean;
}

export const useChat = defineStore("chat", {
  state: (): ChatState => {
    return {
      sessionId2MessageList: {},
      messageMap: {},
      showInsufficientCrystalPopup: false,
      showInsufficientPowerPopup: false,
      exploreScrollY: 0,
      missCrystalAmount: 2,
      missPowerAmount: 20,
      prevGeneratedMsgId: 0,
      replyOptions: [],
      replyGenerating: false,
    };
  },
  getters: {
    messageListBySessionId: (state) => (sessionId: number) => {
      if (!state.sessionId2MessageList[sessionId]) {
        return [];
      }
      return state.sessionId2MessageList[sessionId].keyList.map(
        (messageId) => state.messageMap[messageId]
      );
    },
  },
  actions: {
    updateMap(msgList: ChatMessage[]): ChatMessage[] {
      return msgList.map((msg) => {
        const target = this.messageMap[msg.id];
        if (target) {
          Object.assign(target, msg);
        } else {
          this.messageMap[msg.id] = msg;
        }
        return this.messageMap[msg.id];
      });
    },
    getOrInitSessionItem(mateSessionId: number): {
      keyList: number[];
    } {
      if (!this.sessionId2MessageList[mateSessionId]) {
        this.sessionId2MessageList[mateSessionId] = {
          keyList: [] as number[],
        };
      }
      return this.sessionId2MessageList[mateSessionId];
    },
    async fetchMessageList(
      mateSessionId: number,
      pageSize = 6,
      cursor?: string
    ) {
      const res = await api.readMateMessagePage({
        pagination: { perPage: pageSize },
        filter: { mateSessionId },
        sort: { field: "chat_message.id", order: "desc" },
        cursor,
      });
      this.updateMap(res.page.data);
      return res.page.data;
    },
    async initMessageList(mateSessionId: number) {
      const sessionItem = this.getOrInitSessionItem(mateSessionId);
      const messages = await this.fetchMessageList(mateSessionId, 20);
      const sessionStore = useChatSession();
      sessionStore.setSessionAsRead(mateSessionId);
      sessionItem.keyList = messages.map((msg) => msg.id);
    },
    async sendMessage(
      params: Parameters<typeof api.createOneMateMessage>[0],
      callback: (content: Response<ChatMessage> | string) => void
    ): Promise<void> {
      await api.createOneMateMessage(params, callback);
    },
    async requestImageMessage(
      prevAssistantMsgId: number,
      type: ChatMessageImageType
    ): Promise<ChatMessage> {
      const { item } = await createOneChatImageMessage(prevAssistantMsgId, {
        type,
      });
      return item;
    },
    async getNewestMessage(mateSessionId: number): Promise<number> {
      const sessionItem = this.getOrInitSessionItem(mateSessionId);
      const messageList = this.messageListBySessionId(mateSessionId);
      let cursor: undefined | string = undefined;
      if (messageList.length > 0) {
        cursor = this.useCursorEncode(messageList[0].id, false);
      }
      const messages = await this.fetchMessageList(mateSessionId, 5, cursor);
      if (messages.length === 0) {
        return 0;
      }
      const newMessages = messages.filter(
        (msg) => !sessionItem.keyList.includes(msg.id)
      );
      sessionItem.keyList.unshift(...newMessages.map((msg) => msg.id));
      const sessionStore = useChatSession();
      sessionStore.updatePreviewMessageInSession(mateSessionId, newMessages[0]);
      return newMessages.length;
    },
    async markAsDislike(messageId: number, sessionId: number): Promise<void> {
      const dtIgnore = new Date().toISOString();
      this.messageListBySessionId(sessionId).find(
        (message) => message.id === messageId
      )!.dtIgnore = dtIgnore;
      await api.updateChatMessage(messageId, { dtIgnore });
    },
    async cancelDislike(messageId: number, sessionId: number): Promise<void> {
      this.messageListBySessionId(sessionId).find(
        (message) => message.id === messageId
      )!.dtIgnore = null;
      await api.updateChatMessage(messageId, { dtIgnore: null });
    },
    useCursorEncode(id: number, pointsToNextItems = false): string {
      return btoa(
        JSON.stringify({
          "chat_message.id": id,
          _pointsToNextItems: pointsToNextItems,
        })
      );
    },
    async fetchOlderMessageList(
      mateSessionId: number
    ): Promise<{ hasMore: boolean; count: number }> {
      const sessionItem = this.getOrInitSessionItem(mateSessionId);
      const messageList = this.messageListBySessionId(mateSessionId);
      let cursor = undefined;
      if (messageList.length !== 0) {
        cursor = this.useCursorEncode(
          messageList[messageList.length - 1].id,
          true
        );
      }
      const messages = await this.fetchMessageList(mateSessionId, 10, cursor);
      if (messages.length === 0) {
        return { hasMore: false, count: 0 };
      }
      sessionItem.keyList.push(...messages.map((msg) => msg.id));

      return { hasMore: true, count: messages.length };
    },
    processChatResponse(
      sessionId: number,
      msg: ChatMessage
    ): {
      userMsg?: ChatMessage;
      assistantMsg?: ChatMessage;
    } {
      const mateStore = useMate();
      const sessionItem = this.getOrInitSessionItem(sessionId);
      const userMsg = msg;
      if (!userMsg.mate) {
        userMsg.mate = mateStore.getMyMateById(
          userMsg.mateId
        )! as ChatMessage["mate"];
      }
      const assistantMsg = msg.answer;
      delete userMsg.answer;
      if (userMsg.id) {
        if (userMsg.id in this.messageMap) {
          Object.assign(this.messageMap[userMsg.id], userMsg);
        } else if (
          userMsg.type !== InputTypeEnum.FORWARD &&
          userMsg.type !== InputTypeEnum.CONTINUE
        ) {
          this.messageMap[userMsg.id] = userMsg;
          sessionItem.keyList.unshift(userMsg.id);
        }
      }
      if (assistantMsg) {
        if (assistantMsg.id in this.messageMap) {
          Object.assign(this.messageMap[assistantMsg.id], assistantMsg);
        } else {
          this.messageMap[assistantMsg.id] = assistantMsg;
          sessionItem.keyList.unshift(assistantMsg.id);
        }
      }

      if (msg.additionalMessages) {
        for (const _msg of msg.additionalMessages) {
          if (_msg.id in this.messageMap) {
            Object.assign(this.messageMap[_msg.id], _msg);
          } else {
            this.messageMap[_msg.id] = _msg;
            sessionItem.keyList.unshift(_msg.id);
          }
        }
      }

      const sessionStore = useChatSession();
      if (assistantMsg?.mateSession) {
        sessionStore.updateSessionMap([assistantMsg.mateSession]);
        const { currentCharacterPositions } =
          assistantMsg.mateSession.chatSceneStatus || {};
        if (currentCharacterPositions) {
          const cpMap = new Map<number, number>();
          currentCharacterPositions.forEach((v) => {
            cpMap.set(v.characterId, v.positionId);
          });

          sessionStore.sessionMap[sessionId].chatScene?.characterzz?.forEach(
            (v) =>
              (v.currentPositionId = cpMap.get(v.id!) || v.currentPositionId)
          );
        }
      }
      if (msg.mateSession) {
        sessionStore.updateSessionMap([msg.mateSession]);
      }
      sessionStore.updatePreviewMessageInSession(
        sessionId,
        this.messageMap[sessionItem.keyList[0]]
      );
      return { userMsg, assistantMsg };
    },
    async generateSceneForMsg(matSessionId: number): Promise<void> {
      const res = await api.updateOneChatMessageScene(matSessionId);
      const [chatMessage] = this.updateMap([res.item]);
      if (chatMessage.mateTraitMedia?.bgMediaId) {
        // 有新的场景图 => 刷新场景图列表
        const traitMediaStore = useMateTraitMedia();
        await traitMediaStore.fetchNewerList();
      }
    },
    async generateFaceForMsg(msgId: number): Promise<void> {
      const res = await api.updateOneChatMessageFace(msgId);
      this.updateMap([res.item]);
    },
    async deleteChatMessage(sessionId: number, msgId: number): Promise<void> {
      await api.deleteOneChatMessage(msgId);
      this.sessionId2MessageList[sessionId].keyList =
        this.sessionId2MessageList[sessionId].keyList.filter(
          (id) => id !== msgId
        );
    },
    async updateChatMessage(
      msgId: number,
      params: Parameters<typeof api.updateChatMessage>[1]
    ): Promise<void> {
      const res = await api.updateChatMessage(msgId, params);
      this.updateMap([res.item]);
    },
    async payOneChatMessage(msgId: number): Promise<void> {
      const res = await api.payOneChatMessage(msgId);
      this.updateMap([res.item]);
    },
    async generateChatAudio(id: number, cancelToken?: CancelTokenSource) {
      const res = await api.updateOneChatMessageAudio(id, {
        cancelToken: cancelToken?.token,
      });
      return this.updateMap([res.item])[0];
    },
    getLatestReplyBySessionId(sessionId: number): ChatMessage | undefined {
      const messageList = this.sessionId2MessageList[sessionId].keyList.map(
        (messageId) => this.messageMap[messageId]
      );
      if (messageList.length === 0) {
        return;
      }
      const userStore = useMate();
      return messageList.find(
        (msg) => msg.mateId !== userStore.currentMate?.id
      );
    },
    getMsgByIdx(sessionId: number, idx: number): ChatMessage | undefined {
      const msgId = this.sessionId2MessageList[sessionId]?.keyList[idx] ?? null;
      if (!msgId) return;
      return this.messageMap[msgId];
    },
    async generateReply(
      params: Parameters<typeof ApiGetChatReplyOptions>[0] & {
        messageId?: number;
      }
    ): Promise<"auth" | undefined> {
      try {
        const { messageId, mateSessionId } = params;
        this.replyGenerating = true;
        let msgId = messageId;
        if (!msgId) {
          msgId = this.getLatestReplyBySessionId(mateSessionId)?.id;
        }
        this.prevGeneratedMsgId = msgId || 0;
        const { data } = await ApiGetChatReplyOptions(params);
        this.replyOptions = data.map((item) => item.text) ?? [];
      } catch (e) {
        if (
          (e as AxiosError<ResponseError>)?.response?.data?.code ===
          "SUBSCRIPTION_REQUIRED"
        ) {
          const userStore = useGlobalUser();
          userStore.refreshUser();
          return "auth";
        }
      } finally {
        this.replyGenerating = false;
      }
    },
    async generateReplyOptional(
      params: Parameters<typeof ApiGetChatReplyOptions>[0]
    ) {
      const { mateSessionId } = params;
      const targetMsgId = this.getLatestReplyBySessionId(mateSessionId)?.id;
      if (targetMsgId === this.prevGeneratedMsgId && this.replyGenerating)
        return;
      if (
        (targetMsgId && this.prevGeneratedMsgId !== targetMsgId) ||
        this.replyOptions.length === 0
      ) {
        return this.generateReply(params);
      }
    },
  },
});
